import React, { useState } from 'react';
import { Form, Input, Button, Card, Typography, Alert, Row, Col, Result } from 'antd';
import { MailOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/authService';

const { Title, Text } = Typography;

const ForgotPassword: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const navigate = useNavigate();

  const handleSubmit = async (values: { email: string }) => {
    setLoading(true);
    setError(null);
    try {
      await authService.forgotPassword(values.email);
      setSentTo(values.email);
    } catch (err) {
      // Backend never enumerates; treat any error generically.
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (sentTo) {
    return (
      <div style={{ minHeight: '100vh', background: '#f0f2f5', padding: '40px 0' }}>
        <Row justify="center" align="middle" style={{ minHeight: '60vh' }}>
          <Col xs={22} sm={16} md={12} lg={8} xl={7}>
            <Card>
              <Result
                status="success"
                title="Check your email"
                subTitle={
                  <span>
                    If an account exists for <strong>{sentTo}</strong>, we've sent a link to reset your
                    password. The link expires in 1 hour.
                  </span>
                }
                extra={[
                  <Button type="primary" key="login" onClick={() => navigate('/login')}>
                    Back to Sign In
                  </Button>,
                ]}
              />
            </Card>
          </Col>
        </Row>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f0f2f5', padding: '40px 0' }}>
      <Row justify="center" align="middle" style={{ minHeight: '60vh' }}>
        <Col xs={22} sm={16} md={12} lg={8} xl={7}>
          <Card>
            <div style={{ textAlign: 'center', marginBottom: 24 }}>
              <Title level={2}>Forgot Password</Title>
              <Text type="secondary">Enter your email and we'll send you a reset link</Text>
            </div>

            {error && (
              <Alert message={error} type="error" showIcon closable onClose={() => setError(null)}
                style={{ marginBottom: 16 }} />
            )}

            <Form form={form} name="forgotPassword" onFinish={handleSubmit} layout="vertical" size="large">
              <Form.Item
                name="email"
                label="Email"
                rules={[
                  { required: true, message: 'Please enter your email' },
                  { type: 'email', message: 'Please enter a valid email' },
                ]}
              >
                <Input prefix={<MailOutlined />} placeholder="you@example.com" autoComplete="email" />
              </Form.Item>

              <Form.Item style={{ marginBottom: 8 }}>
                <Button type="primary" htmlType="submit" loading={loading} block size="large">
                  Send Reset Link
                </Button>
              </Form.Item>
            </Form>

            <div style={{ textAlign: 'center', marginTop: 16 }}>
              <Button type="link" size="small" onClick={() => navigate('/login')} style={{ padding: 0 }}>
                Back to Sign In
              </Button>
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default ForgotPassword;
